/**
 * Variable Help Component
 * Feature: 015-custom-prompts
 * Date: 2026-01-04
 *
 * Displays available Handlebars variables for use in custom prompts
 * Shows variable names, descriptions, examples, and required status
 */

import { memo } from 'react';
import { KNOWN_VARIABLES } from '@/@types/prompts';
import { HelpCircle, CheckCircle, Circle } from 'lucide-react';

interface IVariableHelpProps {
  className?: string;
}

const VariableHelp = ({ className = '' }: IVariableHelpProps) => {
  return (
    <div className={`rounded-lg border border-gray-200 bg-gray-50 p-6 ${className}`}>
      {/* Header */}
      <div className="mb-4 flex items-center gap-2">
        <HelpCircle className="h-5 w-5 text-gray-600" />
        <h3 className="text-lg font-semibold text-gray-900">
          Available Variables
        </h3>
      </div>

      {/* Description */}
      <p className="mb-4 text-sm text-gray-600">
        Use these Handlebars-style variables in your prompt template. They will
        be automatically replaced with values from your business profile when
        generating posts.
      </p>

      {/* Variable List */}
      <div className="space-y-3">
        {KNOWN_VARIABLES.map((variable) => (
          <div
            key={variable.name}
            className="rounded-md border border-gray-200 bg-white p-4"
          >
            {/* Variable Name and Required Badge */}
            <div className="mb-2 flex items-center gap-2">
              {variable.required ? (
                <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
              ) : (
                <Circle className="h-4 w-4 text-gray-400 flex-shrink-0" />
              )}
              <code className="rounded bg-gray-100 px-2 py-1 text-sm font-mono text-gray-800">
                {`{{${variable.name}}}`}
              </code>
              {variable.required && (
                <span className="text-xs font-medium text-green-700">
                  Required
                </span>
              )}
              {!variable.required && (
                <span className="text-xs font-medium text-gray-500">
                  Optional
                </span>
              )}
            </div>

            {/* Display Name */}
            <div className="mb-1 text-sm font-medium text-gray-900">
              {variable.displayName}
            </div>

            {/* Description */}
            <div className="mb-2 text-sm text-gray-600">
              {variable.description}
            </div>

            {/* Example */}
            <div className="text-xs text-gray-500">
              <span className="font-medium">Example:</span>{' '}
              <span className="italic">{variable.example}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Usage Instructions */}
      <div className="mt-4 rounded-md bg-blue-50 border border-blue-200 p-4">
        <h4 className="mb-2 text-sm font-semibold text-blue-900">
          Usage Tips
        </h4>
        <ul className="space-y-1 text-sm text-blue-800">
          <li>
            • Use curly braces twice:{' '}
            <code className="bg-blue-100 px-1 rounded">{`{{variable_name}}`}</code>
          </li>
          <li>
            • Optional variables can be used with conditionals:{' '}
            <code className="bg-blue-100 px-1 rounded">{`{{#if company_name}}...{{/if}}`}</code>
          </li>
          <li>
            • Required variables will always have a value from your business
            profile
          </li>
          <li>
            • Optional variables will be replaced with empty string if not set
          </li>
        </ul>
      </div>
    </div>
  );
};

export const VariableHelpMemo = memo(VariableHelp);
